import React                   from 'react';
import Row                     from 'components/App/Row';
import { Col }                 from 'antd';
import { List }                from 'antd';
import { observer }            from 'mobx-react';
import { Button }              from 'antd';
import { MailOutlined }        from '@ant-design/icons';
import CardBox                 from 'components/App/CardBox';
import MessageModel            from 'models/MessageModel';
import DemandMessageModel      from 'models/DemandMessageModel';
import FormattedMessageWrapper from 'components/App/FormattedMessageWrapper';
import Message                 from 'components/Message/Message';
import { ApiCollection }       from 'collections/ApiCollection';
import { AppForm }             from 'components/App/AppForm';
import DemandMessageForm       from 'forms/DemandMessageForm';
import ValidationDemandForm    from 'forms/ValidationDemandForm';
import getAdditionalContent    from '../helpers/getAdditionalContent';
import { IN_PROGRESS }         from 'constants/DemandStatuses';
import RefuseDemandForm        from 'forms/RefuseDemandForm';
import DemandModel             from 'models/DemandModel';
import DemandStatus            from './DemandStatus';
import rights                  from 'utils/rights';
import DemandStatusModel       from 'models/DemandStatusModel';
import { PagedCollection }     from 'collections/PagedCollection';
import DemandLogisticModel     from 'models/DemandLogisticModel';
import MessageMediaModel       from 'models/MessageMediaModel';
import ShowModel               from 'models/ShowModel';
import isMessageEditable       from 'utils/isMessageEditable';

const styleIcon = {
  fontSize: '22px',
  marginRight: 6,
};

interface ExtraProps {
  demand: DemandModel;
  demandStatusCollection: ApiCollection;
  openDemandFormModal: () => void;
  openDemandMessageFormModal: () => void;
  openRefuseDemandFormModal: () => void;
  openValidationDemandFormModal: () => void;
  show: ShowModel;
}

const Extra = observer(
  ({
    demand,
    demandStatusCollection,
    openDemandFormModal,
    openDemandMessageFormModal,
    openRefuseDemandFormModal,
    openValidationDemandFormModal,
    show,
  }: ExtraProps) => {
    const demandStatus = demandStatusCollection.find(ds => ds.id === demand.demandStatusId) as DemandStatusModel;

    const demandStatusInProgress = demandStatus && demandStatus.reference === IN_PROGRESS;

    return (
      <Row>
        {rights.for.demand.newStatus &&
          demandStatus &&
          demandStatus.reference === IN_PROGRESS && (
          <>
            <Col>
              <Button
                className="btn-validate-demand"
                onClick={() => openValidationDemandFormModal()}
                type="primary"
              >
                <FormattedMessageWrapper id="Valider la demande" />
              </Button>
            </Col>
            <Col>
              <Button
                className="btn-refuse-demand"
                onClick={() => openRefuseDemandFormModal()}
                type="primary"
              >
                <FormattedMessageWrapper id="Refuser la demande" />
              </Button>
            </Col>
          </>
        )}
        <Col>
          <Button disabled={!demandStatusInProgress} onClick={() => openDemandFormModal()}>
            <FormattedMessageWrapper id="Modifier la demande" />
          </Button>
        </Col>
        <Col>
          <Button
            disabled={!show.enabled || !rights.for.demandMessage.add}
            onClick={() => openDemandMessageFormModal()}
          >
            <FormattedMessageWrapper id="Envoyer un message" />
          </Button>
        </Col>
      </Row>
    );
  },
);

interface ContentProps {
  demand: DemandModel;
  demandMessagePagedCollection: PagedCollection<DemandMessageModel>;
  demandStatusCollection: ApiCollection<DemandStatusModel>;
  editMessage?: (demandMessage: DemandMessageModel, message: MessageModel) => void;
  messageCollection: ApiCollection<MessageModel>;
  messageMediaCollection: ApiCollection<MessageMediaModel>;
  openRefuseDemandFormModal: () => void;
  openValidationDemandFormModal: () => void;
}

const Content = observer(
  ({
    demand,
    demandMessagePagedCollection,
    demandStatusCollection,
    editMessage,
    messageCollection,
    messageMediaCollection,
    openValidationDemandFormModal,
  }: ContentProps) => {
    const additionalContents: Record<string | number, any> = {};

    if (
      demand.isLoaded &&
      !demand.isLoading &&
      demandMessagePagedCollection.isLoaded &&
      !demandMessagePagedCollection.isLoading &&
      demandStatusCollection.isLoaded &&
      !demandStatusCollection.isLoading
    ) {
      const additionalContent = getAdditionalContent(
        demand,
        demandStatusCollection,
        openValidationDemandFormModal,
      );

      let contentDisplayed = false;

      demandMessagePagedCollection.forEach((demandMessage: DemandMessageModel, demandMessageIndex) => {
        if (
          !contentDisplayed &&
          (additionalContent.status === demandMessage.newDemandStatusId ||
            demandMessageIndex === demandMessagePagedCollection.length - 1)
        ) {
          additionalContents[demandMessage.id?.toString() || ''] = additionalContent.additionalContent;
          contentDisplayed = true;
        } else {
          additionalContents[demandMessage.id?.toString() || ''] = null;
        }
      });
    }

    return (
      <List
        className="messages-list"
        dataSource={demandMessagePagedCollection.models}
        loading={demandMessagePagedCollection.isLoading || messageCollection.isLoading}
        pagination={{
          current: demandMessagePagedCollection.page,
          defaultCurrent: demandMessagePagedCollection.page,
          hideOnSinglePage: true,
          onChange: page => {
            demandMessagePagedCollection.setPage(page);
            demandMessagePagedCollection
              .list()
              .then(() =>
                messageCollection
                  .setFilter('id', demandMessagePagedCollection.getUniques('message.id'))
                  .list(),
              );
          },
          pageSize: demandMessagePagedCollection.itemsPerPage,
          size: 'small',
          total: demandMessagePagedCollection.total,
        }}
        renderItem={(item: DemandMessageModel, itemIndex) => {
          const message = messageCollection.findBy('id', item.messageId) as MessageModel;

          if (!message) {
            return <List.Item key={item.id} />;
          }

          const editMessageEnabled =
            isMessageEditable(itemIndex, message.createdById, message.createdAtDate)
              ? () => editMessage && editMessage(item, message)
              : null;

          let newDemandStatus;
          if (item.newDemandStatusId) {
            newDemandStatus = (
              <DemandStatus
                demand={
                  new DemandModel({
                    'demandStatus.id': item.newDemandStatusId,
                  })
                }
                demandStatusCollection={demandStatusCollection}
              />
            );
          }

          return (
            <List.Item key={item.id}>
              <Message
                additionalContent={additionalContents[item.id?.toString() || '']}
                editMessage={editMessageEnabled}
                lastMessage={itemIndex + 1 === demandMessagePagedCollection.length}
                message={message}
                messageMediaCollection={messageMediaCollection}
                newDemandStatus={newDemandStatus}
              />
            </List.Item>
          );
        }}
        size="large"
      />
    );
  },
);

interface DemandMessageListBoxProps {
  demand: DemandModel;
  demandLogistic?: DemandLogisticModel;
  demandLogisticCollection: ApiCollection<DemandLogisticModel>;
  demandMessagePagedCollection: PagedCollection<DemandMessageModel>;
  demandStatusCollection: ApiCollection<DemandStatusModel>;
  fetchMessages: () => void;
  messageCollection: ApiCollection<MessageModel>;
  messageMediaCollection: ApiCollection<MessageMediaModel>;
  openDemandFormModal: () => void;
  show: ShowModel;
}

class DemandMessageListBox extends React.Component<DemandMessageListBoxProps> {

  public render() {
    const {
      demand,
      demandMessagePagedCollection,
      demandStatusCollection,
      messageCollection,
      messageMediaCollection,
      openDemandFormModal,
      show,
    } = this.props;

    return (
      <div id="DemandMessageListBox">
        <CardBox
          bodyStyle={{
            padding: 0,
          }}
          content={
            <Content
              demand={demand}
              demandMessagePagedCollection={demandMessagePagedCollection}
              demandStatusCollection={demandStatusCollection}
              editMessage={this.openDemandMessageFormModal}
              messageCollection={messageCollection}
              messageMediaCollection={messageMediaCollection}
              openRefuseDemandFormModal={() => this.openRefuseDemandFormModal()}
              openValidationDemandFormModal={() => this.openValidationDemandFormModal()}
            />
          }
          extra={
            <Extra
              demand={demand}
              demandStatusCollection={demandStatusCollection}
              openDemandFormModal={openDemandFormModal}
              openDemandMessageFormModal={() => this.openDemandMessageFormModal()}
              openRefuseDemandFormModal={() => this.openRefuseDemandFormModal()}
              openValidationDemandFormModal={() => this.openValidationDemandFormModal()}
              show={show}
            />
          }
          title={
            <>
              <MailOutlined style={styleIcon} /> <FormattedMessageWrapper id="Messages" />
            </>
          }
        />
      </div>
    );
  }

  private openDemandMessageFormModal = (
    demandMessage: DemandMessageModel = new DemandMessageModel(),
    message: MessageModel = new MessageModel()
  ) => {
    const { demand, fetchMessages } = this.props;

    AppForm.open(DemandMessageForm, {
      demand,
      message,
      model: demandMessage,
      onSuccess: () => {
        fetchMessages();
      },
    }, {
      okText: <FormattedMessageWrapper id="Envoyer" />,
      title: <FormattedMessageWrapper id="Nouveau message" />,
      width: 1024,
    });
  };

  private openRefuseDemandFormModal = (
    demandMessage: DemandMessageModel = new DemandMessageModel(),
    message: MessageModel = new MessageModel()
  ) => {
    const {
      demand,
      demandLogistic,
      demandStatusCollection,
      fetchMessages,
    } = this.props;

    AppForm.open(RefuseDemandForm, {
      demand,
      demandLogistic,
      demandStatusCollection,
      message,
      model: demandMessage,
      onSuccess: () => {
        demand.fetch(); // status could change
        fetchMessages();
      },
    }, {
      okText: <FormattedMessageWrapper id="Refuser" />,
      title: <FormattedMessageWrapper id="Refuser la demande" />,
      width: 1024,
    });
  };

  private openValidationDemandFormModal = (
    demandMessage: DemandMessageModel = new DemandMessageModel(),
    message: MessageModel = new MessageModel()
  ) => {
    const {
      demand,
      demandLogistic,
      demandLogisticCollection,
      demandStatusCollection,
      fetchMessages,
    } = this.props;

    AppForm.open(ValidationDemandForm, {
      demand,
      demandLogistic,
      demandStatusCollection,
      message,
      model: demandMessage,
      onSuccess: () => {
        demand.fetch(); // status could change
        demandLogisticCollection.list();
        fetchMessages();
      },
    }, {
      okText: <FormattedMessageWrapper id="Valider" />,
      title: <FormattedMessageWrapper id="Valider la demande" />,
      width: 1024,
    });
  };
}

export default DemandMessageListBox;
